/*
 * Created Date: Wednesday March 9th 2022
 * Author: Oskar Karnblad
 * License: Proprietary to Scaffcalc AB
 *
 * Copyright (c) 2022 Scaffcalc AB
 */
import { divide, half, minus, plus, round } from "@scaffcalc/packages-shared";
import { Box3, Euler, Vector3 } from "three";
import { Point } from "../factories/PolygonFactory/PolygonFactory.types";

const oneInchToCm: number = 2.54;
const defaultDpi: number = 72;

export const convertPxToM = (pixels: number, dpi: number = defaultDpi) =>
  ((oneInchToCm / dpi) * pixels) / 100;

export const indexOfMax = (arr: Array<number>) => {
  if (arr.length === 0) {
    return -1;
  }

  var max = arr[0];
  var maxIndex = 0;

  for (var i = 1; i < arr.length; i++) {
    if (arr[i] > max) {
      maxIndex = i;
      max = arr[i];
    }
  }

  return maxIndex;
};

export const ceilMatchToArr = (a: number, values: number[]) => {
  /** Variable to return */
  let ceiledValue: number | undefined;

  /** Sort array in ascending order */
  const sortedValues = values.sort();

  /** Return if a is larger than maximum in values (last value in sortedValues) */
  if (a > sortedValues[sortedValues.length - 1]) return;

  /** Iterate over values to find matching ceiled value */
  sortedValues.forEach((value, idx) => {
    /** Return value if a is less than first value */
    if (idx === 0 && a < value) {
      ceiledValue = value;
      return;
    }

    /** Return value if a is the same as value */
    if (a === value) {
      ceiledValue = value;
      return;
    }

    /** Iterate only if we have a nextValue available */
    if (idx < sortedValues.length - 1) {
      const nextValue = sortedValues[plus(idx, 1)];

      if (a > value && a < nextValue) {
        ceiledValue = nextValue;
        return;
      }
    }
  });

  return ceiledValue;
};

export const getDPRUpdate = (factor: number) => round(0.5 + 1.5 * factor, 1);

/** Calculates the angle between three vectors
 * @param startPointVector - Vector3, the start point vector
 * @param middlePointVector - Vector3, the middle point vector
 * @param endPointVector - Vector3, the end point vector
 * @returns number, the angle between the three vectors
 */
export const calculateAngleBetweenVectors = (props: {
  startPointVector: Vector3;
  middlePointVector: Vector3;
  endPointVector: Vector3;
}) => {
  const { startPointVector, middlePointVector, endPointVector } = props;

  const startToMiddleVector = startPointVector.clone().sub(middlePointVector);
  const endToMiddleVector = endPointVector.clone().sub(middlePointVector);

  let angle =
    Math.atan2(startToMiddleVector.z, startToMiddleVector.x) -
    Math.atan2(endToMiddleVector.z, endToMiddleVector.x);

  if (angle < 0) angle += 2 * Math.PI;

  return angle;
};

/** Creates new start and end vectors by applying offset to start and end vectors
 * in the direction of the normal of the line vector between start and end vectors.
 * @param startPosition - Vector3, the start position of the line
 * @param endPosition - Vector3, the end position of the line
 * @param offset - number, the offset to apply to the start and end positions
 * @returns {startPosition: Vector3, endPosition: Vector3}, the new offseted start and end positions
 */
export const createOffsetedVectors = (props: {
  startPosition: Vector3;
  endPosition: Vector3;
  offset: number;
}): { startPosition: Vector3; endPosition: Vector3 } => {
  const { startPosition, endPosition, offset } = props;
  const newStartPosition = startPosition.clone();
  const newEndPosition = endPosition.clone();

  const endBaseVector = newEndPosition.clone().sub(newStartPosition);

  const normalisedEndBaseVector = endBaseVector.clone().normalize();
  const perpendicularVector = normalisedEndBaseVector
    .clone()
    .cross(new Vector3(0, 1, 0));

  const halfDistanceOffset = perpendicularVector.clone().multiplyScalar(offset);

  newStartPosition.sub(halfDistanceOffset);
  newEndPosition.sub(halfDistanceOffset);

  return { startPosition: newStartPosition, endPosition: newEndPosition };
};

/** Creates a Vector3 from a Point object
 * @param point - Point, the point to create a Vector3 from
 * @returns Vector3, the created Vector3
 */
export const createVector3FromPoint = (point: Point, yPos?: number) => {
  const { position } = point;
  const y = yPos || 0;
  return new Vector3(position[0], y, position[1]);
};

/** Calculates the delta value between the unwinded v2 and v1 vectors
 * @param v1 - Vector3, the first vector
 * @param yRotation - number, the yRotation to unwind the vectors with
 * @param v2 - Vector3, the second vector
 * @returns number, the delta value between the unwinded v2 and v1 vectors
 */
export const calculateProjectedVectorDelta = (props: {
  v1: Vector3;
  yRotation: number;
  v2: Vector3;
}) => {
  const { v1, yRotation, v2 } = props;
  const unwindedV1 = v1
    .clone()
    .applyAxisAngle(new Vector3(0, 1, 0), -1 * yRotation);

  const unwindedV2 = v2
    .clone()
    .applyAxisAngle(new Vector3(0, 1, 0), -1 * yRotation);

  const zDelta = minus(unwindedV2.z, unwindedV1.z);
  return zDelta;
};

/** Inflates the polygon point positions from the center of the polygons bounding box with the distance
 * @param positions - Vector3[], the positions of the polygon points
 * @param distance - number, the distance to inflate the polygon points with
 * @returns Vector3[], the inflated polygon point positions
 */
export const bboxCenterInflatePolygonPositions = (props: {
  positions: Vector3[];
  distance: number;
}) => {
  const { positions, distance } = props;

  const bbox = new Box3().setFromPoints(positions);
  const center = bbox.getCenter(new Vector3());

  const newPositions: Vector3[] = [];
  positions.forEach((position) => {
    const normal = position.clone().sub(center).normalize();
    const scaledNormal = normal.clone().multiplyScalar(distance);
    const newPosition = position.clone().add(scaledNormal);
    newPositions.push(newPosition);
  });

  return newPositions;
};

/** Inflates the polygon point by iterating over each point, finding the line vectors to prev and next point,
 * applying the offset to the line vectors in the direction of their normals to calculate their new positions,
 * then calculating the middle vector between the offseted prev and next point positions and offseting the current point
 * in the direction of the middle vector.
 * @param positions - Vector3[], the positions of the polygon points
 * @param distance - number, the distance to inflate the polygon points with
 * @returns Vector3[], the inflated polygon point positions
 */

export const specialInflatePolygonPositions = (props: {
  closedPositions: Vector3[];
  distance: number;
}) => {
  const { closedPositions, distance } = props;
  const newPositions: Vector3[] = [];

  closedPositions.forEach((currentPosition, idx) => {
    const prevPosition = closedPositions[idx - 1];
    const nextPosition = closedPositions[idx + 1];

    if (prevPosition && nextPosition) {
      const newPosition = createNewInflatedPosition({
        prevPosition,
        currentPosition,
        nextPosition,
        distance,
      });

      newPositions.push(newPosition);
    }
  });

  /** Calculate first point */
  if (closedPositions.length < 4) return newPositions;
  const currentPosition = closedPositions[0];
  const prevPosition = closedPositions[closedPositions.length - 2];
  const nextPosition = closedPositions[1];

  const newFirstPointPosition = createNewInflatedPosition({
    prevPosition,
    currentPosition,
    nextPosition,
    distance,
  });
  newPositions.unshift(newFirstPointPosition);

  return [...newPositions, newFirstPointPosition];
};

/** Calculating a inflated current pos by utilising line vectors to prev and next pos,
 * applying the offset to the line vectors in the direction of their normals to calculate their new positions,
 * then calculating the middle vector between the offseted prev and next point positions and offseting the current point
 * in the direction of the middle vector.
 * @param prevPosition - Vector3, the previous position
 * @param currentPosition - Vector3, the current position
 * @param nextPosition - Vector3, the next position
 * @param distance - number, the distance to inflate the polygon points with
 * @returns Vector3, the inflated polygon point position
 * */
const createNewInflatedPosition = (props: {
  prevPosition: Vector3;
  currentPosition: Vector3;
  nextPosition: Vector3;
  distance: number;
}) => {
  const { prevPosition, currentPosition, nextPosition, distance } = props;

  const { endPosition: prevEndPosition } = createOffsetedVectors({
    startPosition: prevPosition,
    endPosition: currentPosition,
    offset: distance,
  });

  const { startPosition: nextStartPosition } = createOffsetedVectors({
    startPosition: currentPosition,
    endPosition: nextPosition,
    offset: distance,
  });

  /** Calculate where endPosition and startPosition meets */
  const currentPosToNewEnd = prevEndPosition.clone().sub(currentPosition);
  const currentPosToNewStart = nextStartPosition.clone().sub(currentPosition);

  const lineVectorLength = currentPosToNewEnd.length();

  const angle = calculateAngleBetweenVectors({
    startPointVector: currentPosToNewStart,
    middlePointVector: new Vector3(),
    endPointVector: currentPosToNewEnd,
  });

  const newLineVectorLength = divide(lineVectorLength, Math.cos(half(angle)));
  const newLineVector = currentPosToNewEnd
    .clone()
    .applyEuler(new Euler(0, -half(angle), 0))
    .normalize()
    .multiplyScalar(newLineVectorLength);

  const newPosition = currentPosition.clone().add(newLineVector);

  return newPosition;
};

/** Calculates if polygon is built in clockwise order by calculating the sum over its edges */
export const isPolygonClockwise = (points: Point[]) => {
  let sum = 0;

  for (let i = 0; i < points.length; i++) {
    const v1 = points[i];
    //v2 becomes the first point if we are at the last point
    const v2 = points[(i + 1) % points.length];
    sum +=
      (v2.position[0] - v1.position[0]) * (v2.position[1] + v1.position[1]);
  }

  // Flipped condition to match our coordinate system
  return sum < 0;
};
